import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../components/SubscriptionGuard';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  FolderKanban,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Archive,
  RotateCcw,
  Copy,
  Grid3X3,
  List,
  Calendar,
  Users,
  Clock,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  X,
  Check,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_COLORS = [
  '#00A3E0', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

export default function AdminProjects() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  
  // State
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ total_projects: 0, active_projects: 0, on_hold_projects: 0, archived_projects: 0, clients: [] });
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // list or grid
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [clientFilter, setClientFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modals
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    color: '#00A3E0',
    client_name: '',
    client_id: '',
    assigned_employees: [],
    project_manager: '',
    estimated_hours: '',
    deadline: '',
    status: 'active',
    billable: true
  });

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch data
  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (clientFilter !== 'all') params.append('client', clientFilter);

      const res = await fetch(`${API_URL}/api/admin/projects?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [token, searchQuery, statusFilter, clientFilter, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/people?limit=100&context=project_assignment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const allEmployees = data.employees || [];
      
      // Filter out test/ghost accounts
      const isTestAccount = (emp) => {
        const testPatterns = [
          /^accept_test/i,
          /^test_/i,
          /deactivate.*test/i,
          /ui.*test/i,
          /@example\.com$/i
        ];
        return testPatterns.some(pattern => 
          pattern.test(emp.email) || pattern.test(emp.name)
        );
      };
      
      const filteredEmployees = allEmployees.filter(emp => !isTestAccount(emp));
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [token, user]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // API returns array directly
      setClients(Array.isArray(data) ? data : (data.clients || []));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProjects(), fetchStats(), fetchEmployees(), fetchClients()]);
      setLoading(false);
    };
    if (token && user) loadData();
  }, [token, user, fetchProjects, fetchStats, fetchEmployees, fetchClients]);

  // Handlers
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const openCreateModal = () => {
    setProjectForm({
      name: '',
      description: '',
      color: '#00A3E0',
      client_name: '',
      client_id: '',
      assigned_employees: [],
      project_manager: '',
      estimated_hours: '',
      deadline: '',
      status: 'active',
      billable: true
    });
    setIsEditing(false);
    setSelectedProject(null);
    setProjectModalOpen(true);
  };

  const openEditModal = (project) => {
    setProjectForm({
      name: project.name,
      description: project.description || '',
      color: project.color || '#00A3E0',
      client_name: project.client_name || '',
      client_id: project.client_id || '',
      assigned_employees: project.assigned_employees || [],
      project_manager: project.project_manager || '',
      estimated_hours: project.estimated_hours || '',
      deadline: project.deadline ? project.deadline.split('T')[0] : '',
      status: project.status || 'active',
      billable: project.billable !== false
    });
    setSelectedProject(project);
    setIsEditing(true);
    setProjectModalOpen(true);
  };

  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (!projectForm.client_id) {
      toast.error('Client is required');
      return;
    }
    if (!projectForm.assigned_employees || projectForm.assigned_employees.length === 0) {
      toast.error('Please assign at least one team member');
      return;
    }

    try {
      const payload = {
        ...projectForm,
        estimated_hours: projectForm.estimated_hours ? parseFloat(projectForm.estimated_hours) : null,
        deadline: projectForm.deadline || null
      };

      const url = isEditing 
        ? `${API_URL}/api/admin/projects/${selectedProject.id}`
        : `${API_URL}/api/admin/projects`;
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save project');
      }

      toast.success(isEditing ? 'Project updated' : 'Project created');
      setProjectModalOpen(false);
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleArchive = async (project) => {
    try {
      await fetch(`${API_URL}/api/admin/projects/${project.id}/archive`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project archived');
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleRestore = async (project) => {
    try {
      await fetch(`${API_URL}/api/admin/projects/${project.id}/restore`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project restored');
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error('Failed to restore project');
    }
  };

  const handleDuplicate = async (project) => {
    try {
      await fetch(`${API_URL}/api/admin/projects/${project.id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project duplicated');
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error('Failed to duplicate project');
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${selectedProject.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete');
      }

      toast.success('Project and all time entries deleted');
      setDeleteModalOpen(false);
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleEmployeeAssignment = (employeeId) => {
    setProjectForm(prev => {
      const newAssigned = prev.assigned_employees.includes(employeeId)
        ? prev.assigned_employees.filter(id => id !== employeeId)
        : [...prev.assigned_employees, employeeId];
      return {
        ...prev,
        assigned_employees: newAssigned,
        project_manager: newAssigned.includes(prev.project_manager) ? prev.project_manager : ''
      };
    });
  };

  // Helpers
  const getHealthIndicator = (project) => {
    if (!project.estimated_hours) return null;
    const percentage = (project.total_hours / project.estimated_hours) * 100;
    if (percentage >= 100) return { color: 'bg-red-50 dark:bg-red-900/30', label: 'Over budget' };
    if (percentage >= 75) return { color: 'bg-amber-50 dark:bg-amber-900/30', label: 'At risk' };
    return { color: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'On track' };
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'on_hold': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'archived': return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const getProgressPercentage = (project) => {
    if (!project.estimated_hours || project.estimated_hours === 0) return 0;
    return Math.min((project.total_hours / project.estimated_hours) * 100, 100);
  };

  const SortHeader = ({ column, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('nav.projects')}</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{t('admin.projectsSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-lg p-1">
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
                data-testid="view-grid-btn"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </div>
            <Button onClick={openCreateModal} data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:border-blue-500/50" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <FolderKanban className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_projects}</p>
                  <p className="text-sm text-slate-500">Total Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-emerald-500/50" onClick={() => setStatusFilter('active')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.active_projects}</p>
                  <p className="text-sm text-slate-500">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-amber-500/50" onClick={() => setStatusFilter('on_hold')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.on_hold_projects}</p>
                  <p className="text-sm text-slate-500">On Hold</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {user?.role === 'admin' && (
            <Card className="cursor-pointer hover:border-slate-500/50" onClick={() => setStatusFilter('archived')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 dark:bg-slate-50 dark:bg-slate-7000/20 rounded-lg">
                    <Archive className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.archived_projects}</p>
                    <p className="text-sm text-slate-500">Archived</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="client-filter">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {stats.clients?.map(client => (
                      <SelectItem key={client} value={client}>{client}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects List/Grid */}
        {viewMode === 'list' ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="projects-table">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <SortHeader column="name" label="Project" />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                      {!isFreePlan && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Project Manager</th>}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned</th>
                      <SortHeader column="total_hours" label="Hours" />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                      <SortHeader column="deadline" label="Deadline" />
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {projects.map((project) => {
                      const health = getHealthIndicator(project);
                      const progress = getProgressPercentage(project);
                      return (
                        <tr 
                          key={project.id} 
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          data-testid={`project-row-${project.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: project.color }}
                              />
                              {health && (
                                <div 
                                  className={`w-2 h-2 rounded-full ${health.color}`} 
                                  title={health.label}
                                />
                              )}
                              <button
                                onClick={() => navigate(`/admin/projects/${project.id}`)}
                                className="font-medium text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 text-left"
                              >
                                {project.name}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {project.client_name || '-'}
                          </td>
                          {!isFreePlan && (
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {project.project_manager_name || '-'}
                          </td>
                          )}
                          <td className="px-4 py-3">
                            <Badge className={getStatusBadgeColor(project.status)}>
                              {project.status === 'on_hold' ? 'On Hold' : project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600 dark:text-slate-300">{project.assigned_count || 0}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {project.total_hours?.toFixed(1) || '0'}h
                            {project.estimated_hours && (
                              <span className="text-slate-400"> / {project.estimated_hours}h</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {project.estimated_hours ? (
                              <div className="w-24">
                                <Progress value={progress} className="h-2" />
                                <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {project.deadline ? new Date(project.deadline).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`project-actions-${project.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/admin/projects/${project.id}`)}>
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditModal(project)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {user?.role === 'admin' && (
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => {
                                      setSelectedProject(project);
                                      setDeleteModalOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                    {projects.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                          No projects found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Grid View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => {
              const health = getHealthIndicator(project);
              const progress = getProgressPercentage(project);
              return (
                <Card 
                  key={project.id} 
                  className="hover:border-blue-500/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/projects/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: project.color }}
                        />
                        <h3 className="font-semibold text-slate-900 dark:text-white">{project.name}</h3>
                        {health && (
                          <div 
                            className={`w-2 h-2 rounded-full ${health.color}`} 
                            title={health.label}
                          />
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => openEditModal(project)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(project)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user?.role === 'admin' && (
                            project.status === 'archived' ? (
                              <DropdownMenuItem onClick={() => handleRestore(project)}>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restore
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleArchive(project)}>
                                <Archive className="w-4 h-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            )
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {project.client_name && (
                      <p className="text-sm text-slate-500 mb-3">{project.client_name}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 mb-3">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {project.assigned_count || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {project.total_hours?.toFixed(1) || '0'}h
                      </div>
                      {project.deadline && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(project.deadline).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Badge className={getStatusBadgeColor(project.status)}>
                        {project.status === 'on_hold' ? 'On Hold' : project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
                      </Badge>
                      {project.estimated_hours && (
                        <div className="flex items-center gap-2">
                          <Progress value={progress} className="w-16 h-2" />
                          <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {projects.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                No projects found
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Project Modal */}
        <Dialog open={projectModalOpen} onOpenChange={setProjectModalOpen}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Project' : 'Create Project'}</DialogTitle>
              <DialogDescription>
                {isEditing ? 'Update project details' : 'Add a new project to your workspace'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Project Name */}
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="e.g. Website Redesign"
                  data-testid="project-name-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Brief description of the project..."
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setProjectForm({ ...projectForm, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        projectForm.color === color 
                          ? 'border-slate-900 dark:border-white scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Client */}
              <div className="space-y-2">
                <Label>Client</Label>
                <Select
                  value={projectForm.client_id || 'no_client'}
                  onValueChange={(value) => {
                    if (value === 'no_client') {
                      setProjectForm({ ...projectForm, client_name: '', client_id: '' });
                    } else {
                      const client = clients.find(c => c.id === value);
                      setProjectForm({ ...projectForm, client_name: client?.name || '', client_id: value });
                    }
                  }}
                >
                  <SelectTrigger data-testid="project-client-select">
                    <SelectValue placeholder="Select a client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_client">No Client</SelectItem>
                    {clients.length > 0 ? (
                      clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: client.color || '#6B7280' }}
                            />
                            {client.name}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-3 text-sm text-slate-500 text-center">
                        <p>No clients added yet</p>
                        <button 
                          type="button"
                          className="text-blue-600 hover:underline mt-1 block w-full"
                          onClick={() => { setProjectModalOpen(false); navigate('/admin/clients'); }}
                        >
                          Add a client
                        </button>
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Assigned Employees */}
              <div className="space-y-2">
                <Label>Assigned Employees</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {employees.map((emp) => (
                    <label
                      key={emp.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={projectForm.assigned_employees.includes(emp.id)}
                        onChange={() => toggleEmployeeAssignment(emp.id)}
                        className="rounded border-slate-300"
                      />
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium overflow-hidden">
                        {emp.profile_photo ? (
                          <img src={emp.profile_photo} alt={emp.name} className="w-full h-full object-cover" />
                        ) : (
                          emp.name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.email}</p>
                      </div>
                    </label>
                  ))}
                  {employees.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">No employees found</p>
                  )}
                </div>
                <p className="text-xs text-slate-500">{projectForm.assigned_employees.length} selected</p>
              </div>

              {/* Project Manager */}
              {!isFreePlan && projectForm.assigned_employees.length > 0 && (
                <div className="space-y-2">
                  <Label>Project Manager</Label>
                  <Select
                    value={projectForm.project_manager || 'none'}
                    onValueChange={(v) => setProjectForm({ ...projectForm, project_manager: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="project-manager-select">
                      <SelectValue placeholder="Select project manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project Manager</SelectItem>
                      {employees
                        .filter(emp => projectForm.assigned_employees.includes(emp.id))
                        .map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} ({emp.role})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Choose from assigned team members</p>
                </div>
              )}

              {/* Estimated Hours and Deadline */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    value={projectForm.estimated_hours}
                    onChange={(e) => setProjectForm({ ...projectForm, estimated_hours: e.target.value })}
                    placeholder="e.g. 100"
                    data-testid="project-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={projectForm.deadline}
                    onChange={(e) => setProjectForm({ ...projectForm, deadline: e.target.value })}
                    data-testid="project-deadline-input"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={projectForm.status} 
                  onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}
                >
                  <SelectTrigger data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Billable (Admin only) */}
              {user?.role === 'admin' && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">Billable Project</Label>
                    <p className="text-xs text-slate-500">For admin tracking purposes only</p>
                  </div>
                  <Switch
                    checked={projectForm.billable}
                    onCheckedChange={(checked) => setProjectForm({ ...projectForm, billable: checked })}
                    data-testid="project-billable-toggle"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProjectModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveProject} data-testid="save-project-btn">
                {isEditing ? 'Save Changes' : 'Create Project'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent data-testid="delete-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Delete Project
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{selectedProject?.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-500/20">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> All time entries tracked to this project will be permanently deleted.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
